/* eslint-disable react/jsx-no-target-blank */
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import { isLineBreakStyle } from '@src/editor/services/captionLineBreakStyle';
import {
  LINE_LENGTH_PRESETS,
  resolveConfiguredMaxCharLength,
} from '@src/editor/services/karaokeSegmentation';
import {
  DEFAULT_KARAOKE_CONFIG,
  DEFAULT_TEXT_TRANSCRIPT_CONFIG,
  MAX_CHARS_INSTAGRAM,
} from '@src/services/karaoke/karaokeConstants';
import { useTranscription } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { findOriginalVideoElementAtCurrentTime } from '@src/utility/elements';
import {
  convertFromPixels,
  convertToPixels,
  getMinMaxYPosition,
} from '@src/utility/videoTranscriptionUtils';
import lodash from 'lodash';
import { observer } from 'mobx-react-lite';
import { forwardRef, useMemo, useState } from 'react';
import styled from 'styled-components';

import UpDownIcon from '../../../svgs/UpDownIcon';
import { FONT_SIZE_VALUES } from '../../../utility/general';
import Checkbox from '../../common/Checkbox';
import ColorDropdownSelect from '../../common/ColorDropdownSelect';
import FontFamilyVariantDropdown from '../../common/FontFamilyVariantDropdown';
import { CommaIcon, PeriodIcon } from '../../common/icons';
import ItemDropdownSelect from '../../common/ItemDropdownSelect';
import KaraokeShadow from '../../common/KaraokeShadow';
import StrokeSelect from '../../common/StrokeSelect';
import TextBackgroundRadius from '../../common/TextBackgroundRadius';
import SliderSelect from '../SliderSelect';
import AlignFormatting from './AlignFormatting';
import AlignThresholdControl from './AlignThresholdControl';
import BackgroundColorOpacityControl from './BackgroundColorOpacityControl';
import BoldItalicCapitalize from './BoldItalicCapitalize';
import { useKaraokeSceneTextPosition } from './useKaraokeSceneTextPosition';

import type { ElementState } from '../../../renderer/ElementState';
import type { KaraokeShadowConfig } from '../../common/KaraokeShadow';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface TextSettingsProps {
  activeElement?: ElementState;
  skipFields: string[];
  selectedBold: string | null;
  selectedFontWeight: string;
  selectedActions: (Action | Readability)[];
  selectedFontFamily: string;
  selectedShadow?: KaraokeShadowConfig;
  modifyProperty: (propertyMap: Partial<KaraokeConfig>) => void;
  handleSetFont: (
    fontFamily: string | undefined,
    variant?: { weight: number; style?: 'normal' | 'italic' },
  ) => void;
  handleSetBold: (
    bold: string,
    fontWeight: number,
    fontScriptName?: string,
  ) => void;
  handleSanitizePunctuation: (action: Readability) => void;
  handleToggleAction: (
    action: Action,
    propertyName: keyof Pick<KaraokeConfig, 'font_style' | 'text_transform'>,
    normalState: string,
    requiredState: string,
  ) => void;
  alignType: Align;
  toggleAlign: (align: Align) => void;
  setAnimation: (
    time: string | number | null,
    type: unknown,
    duration?: number | null,
    background_effect?: string | null,
  ) => void;
  animations: {
    enter?: string;
    exit?: string;
    element?: string;
  };
  karaokeElement: KaraokeConfig;
  hasStickyBottom: boolean;
  editorStore: EditorStoreLike;
}

export enum Action {
  bold = 'Bold',
  italic = 'Italic',
  capitalize = 'Capitalize',
  align = 'Align',
}

export enum Readability {
  comma = 'Comma',
  period = 'Period',
  filler = 'Fillers',
}

export enum Align {
  leftAlign = 'Left Align',
  midAlign = 'Middle Align',
  rightAlign = 'Right Align',
}

export const AlignValues = {
  [Align.leftAlign]: '0%',
  [Align.midAlign]: '50%',
  [Align.rightAlign]: '100%',
};

const COLLAPSIBLE_SECTIONS = {
  quickSettings: 'quickSettings',
  readability: 'readability',
  fontAndText: 'fontAndText',
  position: 'position',
  advancedStyling: 'advancedStyling',
} as const;

type CollapsibleSectionKey =
  (typeof COLLAPSIBLE_SECTIONS)[keyof typeof COLLAPSIBLE_SECTIONS];

// Animation types available for captions
const TextAnimationTypes = {
  'text-appear': 'Appear',
  'text-typewriter': 'Typewriter',
};

export const TextSettingsUI = observer(
  forwardRef<HTMLDivElement, TextSettingsProps>((props, ref) => {
    const {
      selectedBold,
      selectedActions,
      selectedFontFamily,
      selectedFontWeight,
      selectedShadow,
      modifyProperty,
      handleSetFont,
      handleSetBold,
      handleToggleAction,
      handleSanitizePunctuation,
      alignType,
      toggleAlign,
      karaokeElement,
      hasStickyBottom,
      editorStore,
      setAnimation,
      animations,
    } = props;

    const {
      font_size: selectedFontSize,
      fill_color: selectedFillColor,
      background_color: selectedBackgroundColor,
      background_align_threshold: selectedBackgroundAlignThreshold,
      background_border_radius: selectedBorderRadius,
    } = karaokeElement;

    const videoCreator = useVideoCreatorStore();
    const transcriptionStore = useTranscription();
    const preferredElementId = useMemo(() => {
      const el = findOriginalVideoElementAtCurrentTime(videoCreator);
      return el?.id ?? undefined;
    }, [videoCreator.time, videoCreator.rootStore.videoSourceStore.source]);

    // Helper function to modify text element properties using headless editor or fallback to legacy
    const modifyPropertyWithHeadless = (property: Record<string, unknown>) => {
      if (editorStore?.editor && props.activeElement?.source?.id) {
        // Use headless editor UpdateElementCommand
        const success = editorStore.editor.commandUpdateElement(
          props.activeElement.source.id,
          property,
        );

        if (!success) {
          console.warn(`Failed to update text element properties:`, property);
        }
      } else {
        // Fallback to legacy method via passed modifyProperty function
        modifyProperty(property);
      }
    };

    const modifyPropertyDebounced = lodash.debounce(
      modifyPropertyWithHeadless,
      500,
    );

    const {
      textPositionEffective,
      updateTextPosition,
      hasSceneOverride,
      setSceneOverride,
      sceneCount,
    } = useKaraokeSceneTextPosition({
      editorStore,
      currentTime: videoCreator.time,
      basePosition: {
        x: karaokeElement.x,
        y: karaokeElement.y,
        x_alignment: karaokeElement.x_alignment,
      },
      preferredElementId,
      sceneLayoutIndexStore: videoCreator.rootStore.sceneLayoutIndexStore,
    });

    const [openSections, setOpenSections] = useState<
      Record<CollapsibleSectionKey, boolean>
    >({
      [COLLAPSIBLE_SECTIONS.quickSettings]: false,
      [COLLAPSIBLE_SECTIONS.readability]: false,
      [COLLAPSIBLE_SECTIONS.fontAndText]: false,
      [COLLAPSIBLE_SECTIONS.position]: false,
      [COLLAPSIBLE_SECTIONS.advancedStyling]: false,
    });

    const toggleSection = (section: CollapsibleSectionKey) => {
      setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const transcriptionLanguage =
      transcriptionStore.originalTranscription?.language;
    const selectedLanguage =
      karaokeElement.language || DEFAULT_KARAOKE_CONFIG.language!;
    const originalLanguageOption = ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
      option => option.value === transcriptionLanguage,
    ) ?? {
      caption: 'Original',
      value: 'original',
    };

    const selectedBackgroundOpacity = parseFloat(
      !selectedBackgroundColor || selectedBackgroundColor === 'transparent'
        ? '0'
        : (selectedBackgroundColor.split(',')[3]?.split(')')[0]?.trim() ?? '1'),
    );

    // The line-length budget this config selects, with a legacy budget mapped to
    // its modern equivalent so an existing story still shows the preset it was
    // set to (the stored number itself is left alone until the user picks one).
    const rawLineLength = karaokeElement.transcript_maximum_length;
    const selectedLineLength =
      rawLineLength == null
        ? null
        : (resolveConfiguredMaxCharLength(rawLineLength) ?? rawLineLength);
    // A Punctuation style overrides the length, so it is what the Words Per
    // Line dropdown shows when set.
    const lineBreakStyle = karaokeElement.line_break_style ?? null;

    const strokeWidth = parseFloat(
      karaokeElement.stroke_width ?? DEFAULT_KARAOKE_CONFIG.stroke_width,
    );
    const strokeColor =
      karaokeElement.stroke_color || DEFAULT_KARAOKE_CONFIG.stroke_color;

    const minMaxXPosition = (() => {
      let min = 0;
      let max = 100;
      let width = videoCreator.rootStore.videoSourceStore.source?.width || 1280;
      let height =
        videoCreator.rootStore.videoSourceStore.source?.height || 720;
      const pxPerChar = 15 / 20; // 14px per char for 20px font size
      const fontSize = parseFloat(selectedFontSize);
      const fontSizeUnits = selectedFontSize.match(/[a-z]+/)?.[0] || 'px';
      const fontSizeInPx =
        fontSizeUnits === 'vh'
          ? convertToPixels(fontSize, 'vh', { height, width })
          : fontSize;
      if (karaokeElement.instagramEffect) {
        const largeFontSize = 1.25 * fontSizeInPx;
        const dx =
          100 -
          MAX_CHARS_INSTAGRAM *
            convertFromPixels(largeFontSize * pxPerChar, 'vw', {
              height,
              width,
            });
        if (alignType === Align.midAlign) {
          min = 47 - 0.5 * dx;
          max = 51 + 0.5 * dx;
        } else if (alignType === Align.leftAlign) {
          min = 45;
          max = 50 + dx;
        } else if (alignType === Align.rightAlign) {
          min = 50 - dx;
          max = 56;
        }
      } else {
        min = 40;
        max = 60;
      }
      return [min, max];
    })();

    const minMaxYPosition = getMinMaxYPosition(
      undefined,
      selectedFontSize,
      karaokeElement.instagramLines || 5,
      karaokeElement,
    );

    const karaokeTextSettings =
      videoCreator.karaokeProducer.getKaraokeTextSettingByAspectRatio();

    const handleSliderTextPosition = (propertyMap: Partial<KaraokeConfig>) => {
      const entries = Object.entries(propertyMap);
      if (entries.length === 0) return;

      if (entries.length === 1) {
        const [propertyName, value] = entries[0];
        if (
          propertyName === 'x' ||
          propertyName === 'y' ||
          propertyName === 'x_alignment'
        ) {
          updateTextPosition(propertyName, value, modifyPropertyDebounced);
          return;
        }
      }

      // Fallback to regular debounced update for multi-key maps or unsupported keys.
      modifyPropertyDebounced(propertyMap);
    };

    const effectiveAlignType = useMemo(() => {
      const alignValue =
        textPositionEffective.x_alignment ?? AlignValues[Align.midAlign];
      if (alignValue === AlignValues[Align.leftAlign]) return Align.leftAlign;
      if (alignValue === AlignValues[Align.rightAlign]) return Align.rightAlign;
      return Align.midAlign;
    }, [textPositionEffective.x_alignment]);

    const handleSettingChange = (
      propertyName: string,
      value: string | number | null | undefined,
    ) => {
      const patch = {
        [propertyName]: value,
      } as Partial<KaraokeConfig>;
      modifyProperty(patch);
    };

    return (
      <Main>
        {/* {!skipFields.includes('text') && props.activeElement && (
        <TextInput activeElement={props.activeElement} />
      )} */}
        {/* ______________NEW______________ */}
        {/* Quick Settings */}
        <CollapsibleSection>
          <CollapsibleHeader
            isOpen={openSections[COLLAPSIBLE_SECTIONS.quickSettings]}
            onClick={() => toggleSection(COLLAPSIBLE_SECTIONS.quickSettings)}
          >
            <CollapsibleTitle>Quick Settings</CollapsibleTitle>
            <ChevronDownIcon
              width="12"
              height="12"
              strokeColor="currentColor"
            />
          </CollapsibleHeader>
          {openSections[COLLAPSIBLE_SECTIONS.quickSettings] && (
            <CollapsibleContent>
              <SliderGroup>
                <Section>
                  <SectionCaption>Animation style</SectionCaption>
                  <ItemDropdownSelect
                    action={(propertyName, value) =>
                      handleSettingChange(propertyName, value || undefined)
                    }
                    propertyName="transcript_effect"
                    defaultValue={
                      (karaokeElement.transcript_effect as string) || null
                    }
                    values={[
                      { value: null, caption: 'None' },
                      { value: 'color', caption: 'Color' },
                      { value: 'karaoke', caption: 'Karaoke' },
                      { value: 'highlight', caption: 'Highlight' },
                      { value: 'enlarge', caption: 'Enlarge' },
                    ]}
                  />
                </Section>
                <Section>
                  <SectionCaption>Animation Color</SectionCaption>
                  <ColorDropdownSelect
                    action={handleSettingChange}
                    propertyName="transcript_color"
                    defaultValue={
                      karaokeElement.transcript_color ||
                      DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color
                    }
                  />
                </Section>
              </SliderGroup>
              <SliderGroup>
                <Section>
                  <SectionCaption>Font text Color</SectionCaption>
                  <ColorDropdownSelect
                    action={(propertyName, value) =>
                      modifyPropertyWithHeadless({ [propertyName]: value })
                    }
                    propertyName="fill_color"
                    defaultValue={selectedFillColor}
                  />
                </Section>
                <Section>
                  <SectionCaption>Background Color</SectionCaption>
                  <Highlight>
                    <BackgroundColorOpacityControl
                      action={modifyPropertyWithHeadless}
                      defaultValue={selectedBackgroundColor}
                      opacity={selectedBackgroundOpacity}
                    />
                  </Highlight>
                </Section>
              </SliderGroup>
            </CollapsibleContent>
          )}
        </CollapsibleSection>

        {/* Quick Settings */}

        <CollapsibleSection>
          <CollapsibleHeader
            isOpen={openSections[COLLAPSIBLE_SECTIONS.readability]}
            onClick={() => toggleSection(COLLAPSIBLE_SECTIONS.readability)}
          >
            <CollapsibleTitle>Readability</CollapsibleTitle>
            <ChevronDownIcon
              width="12"
              height="12"
              strokeColor="currentColor"
            />
          </CollapsibleHeader>
          {openSections[COLLAPSIBLE_SECTIONS.readability] && (
            <CollapsibleContent>
              <Section>
                <SectionCaption>Display Options</SectionCaption>
                <ActionGroup>
                  {Object.values(Readability).map(action => (
                    <ActionButton
                      key={action}
                      isSelected={selectedActions.includes(action)}
                      onClick={() => handleSanitizePunctuation(action)}
                    >
                      {(() => {
                        switch (action) {
                          case Readability.comma:
                            return <CommaIcon fillColor={'currentColor'} />;
                          case Readability.period:
                            return <PeriodIcon fillColor={'currentColor'} />;
                          case Readability.filler:
                            return <span className="filler">Filler</span>;
                        }
                      })()}
                    </ActionButton>
                  ))}
                </ActionGroup>
              </Section>
              <Section>
                <SectionCaption>Words Per Line</SectionCaption>
                <ItemDropdownSelect
                  action={(_propertyName, value) => {
                    if (isLineBreakStyle(value)) {
                      handleSettingChange('line_break_style', value);
                      return;
                    }
                    modifyProperty({
                      // Leaving a Punctuation style: clear it so the length applies.
                      ...(lineBreakStyle && { line_break_style: null }),
                      transcript_maximum_length:
                        value == null ? null : Number(value),
                    });
                  }}
                  propertyName="transcript_maximum_length"
                  defaultValue={
                    lineBreakStyle ??
                    (selectedLineLength == null
                      ? null
                      : String(selectedLineLength))
                  }
                  values={(() => {
                    const values = [
                      {
                        value: null,
                        caption: 'Default (Transcript Line Break)',
                      },
                      {
                        value: 'punctuation',
                        caption: 'Punctuation',
                        info: 'Starts a new line at commas and sentence ends.',
                      },
                      {
                        value: 'punctuation_no_commas',
                        caption: 'Punctuation (No Commas)',
                        info: 'Starts a new line only at sentence ends, so lines run longer.',
                      },
                      {
                        value: String(LINE_LENGTH_PRESETS.oneWord),
                        caption: '1 word per line',
                      },
                      {
                        value: String(LINE_LENGTH_PRESETS.twoWords),
                        caption: '2 words per line',
                      },
                      {
                        value: String(LINE_LENGTH_PRESETS.threeWords),
                        caption: '3 words per line',
                      },
                      {
                        value: String(LINE_LENGTH_PRESETS.fiveWords),
                        caption: '5 words per line',
                      },
                    ];

                    // in case value is custom (shouldn't happen), add it to the list
                    if (
                      !lineBreakStyle &&
                      values.every(v => v.value != selectedLineLength)
                    ) {
                      values.push({
                        value: String(selectedLineLength),
                        caption:
                          selectedLineLength ==
                          DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_maximum_length
                            ? 'Default (Transcript Line Break)'
                            : `Custom length: ${selectedLineLength}`,
                      });
                    }

                    return values;
                  })()}
                />
              </Section>
            </CollapsibleContent>
          )}
        </CollapsibleSection>

        {/* Font and Text */}

        <CollapsibleSection>
          <CollapsibleHeader
            isOpen={openSections[COLLAPSIBLE_SECTIONS.fontAndText]}
            onClick={() => toggleSection(COLLAPSIBLE_SECTIONS.fontAndText)}
          >
            <CollapsibleTitle>Font and Text</CollapsibleTitle>
            <ChevronDownIcon
              width="12"
              height="12"
              strokeColor="currentColor"
            />
          </CollapsibleHeader>
          {openSections[COLLAPSIBLE_SECTIONS.fontAndText] && (
            <CollapsibleContent>
              <SliderGroup>
                <Section>
                  <SectionCaption>Font</SectionCaption>
                  {/*
                   * Uses the gold-standard picker (chevron-collapsed
                   * family rows with inline variant chips, each rendered
                   * in its own weight). Closed state shows "Family ·
                   * Variant" so the caption surface matches the pattern
                   * shipped for composition templates and Graphics →
                   * Text. The legacy `FontDropdownSelect` is gone after
                   * this swap.
                   */}
                  <FontFamilyVariantDropdown
                    action={(_, value, variant) =>
                      handleSetFont(value || undefined, variant)
                    }
                    propertyName="font_family"
                    defaultValue={selectedFontFamily}
                    // Lets the picker mark the active weight row for
                    // popular/script fonts (brand variants match on
                    // postScriptName and don't need it).
                    fontWeight={selectedFontWeight}
                  />
                </Section>
              </SliderGroup>
              {/*
               * Font Size sits on its own full-width row *below* Font Family.
               * It used to share a flex row with the family picker at 40%
               * width, but the picker's closed-state label ("Family ·
               * Variant") doesn't shrink, so the size control got pushed off
               * the panel edge and clipped. Stacking avoids the contention.
               */}
              <SliderGroup>
                <Section>
                  <SectionCaption>Font Size</SectionCaption>
                  <ItemDropdownSelect
                    action={(propertyName, value) =>
                      modifyPropertyWithHeadless({ [propertyName]: value })
                    }
                    propertyName="font_size"
                    defaultValue={videoCreator.textProducer
                      .getFontSize(selectedFontSize)
                      .toString()}
                    values={FONT_SIZE_VALUES.map(value => ({
                      caption: `${value}`,
                      value: `${value}`,
                    }))}
                  />
                </Section>
              </SliderGroup>
              <SliderGroup>
                <Section>
                  <SectionCaption>Formatting</SectionCaption>
                  <SectionGroup style={{ gap: '8px' }}>
                    <BoldItalicCapitalize
                      ref={ref}
                      selectedActions={selectedActions as Action[]}
                      selectedFontFamily={selectedFontFamily}
                      selectedBold={selectedBold}
                      hasStickyBottom={hasStickyBottom}
                      handleSetBold={handleSetBold}
                      handleToggleAction={handleToggleAction}
                    />
                  </SectionGroup>
                </Section>
              </SliderGroup>
              {/*
               * Legacy two-value language dropdown (Original / English).
               * Hidden when the captions panel toggle (PLAT-9621) has
               * selected a translation language — the new toggle is the
               * canonical control in that mode and showing both creates
               * a dual source of truth. Full removal of the legacy
               * dropdown rides with T48 / T49.
               */}
              {!transcriptionLanguage?.includes('en') &&
                (selectedLanguage === 'original' ||
                  selectedLanguage === 'english') && (
                  <SliderGroup>
                    <Section>
                      <SectionCaption>Language</SectionCaption>
                      <div style={{ width: '177px', marginBottom: '8px' }}>
                        <ItemDropdownSelect
                          action={(propertyName, value) =>
                            modifyPropertyWithHeadless({
                              [propertyName]: value,
                            })
                          }
                          propertyName="language"
                          defaultValue={
                            selectedLanguage.includes('en')
                              ? 'english'
                              : 'original'
                          }
                          values={[
                            {
                              value: 'original',
                              caption: originalLanguageOption.caption,
                            },
                            { value: 'english', caption: 'English' },
                          ]}
                        />
                      </div>
                    </Section>
                  </SliderGroup>
                )}
            </CollapsibleContent>
          )}
        </CollapsibleSection>

        {/* Position */}

        <CollapsibleSection>
          <CollapsibleHeader
            isOpen={openSections[COLLAPSIBLE_SECTIONS.position]}
            onClick={() => toggleSection(COLLAPSIBLE_SECTIONS.position)}
          >
            <CollapsibleTitle>Position</CollapsibleTitle>
            <ChevronDownIcon
              width="12"
              height="12"
              strokeColor="currentColor"
            />
          </CollapsibleHeader>
          {openSections[COLLAPSIBLE_SECTIONS.position] && (
            <CollapsibleContent>
              <SliderGroup>
                <Section>
                  <SectionCaption>Vertical Position</SectionCaption>
                  <SliderSelect
                    title="Position"
                    Icon={<UpDownIcon strokeColor="var(--accent)" />}
                    action={handleSliderTextPosition}
                    defaultValue="80"
                    unit="%"
                    currValue={parseFloat(
                      textPositionEffective.y || karaokeElement.y,
                    )}
                    propertyName="y"
                    reverse={true}
                    min={100 - minMaxYPosition[1]}
                    max={100 - minMaxYPosition[0]}
                    snapPoints={[
                      {
                        value: parseFloat(
                          karaokeTextSettings.y || DEFAULT_KARAOKE_CONFIG.y,
                        ),
                        tooltip: 'Safe Zone',
                        reverse: true,
                      },
                      {
                        value:
                          (100 -
                            minMaxYPosition[1] +
                            100 -
                            minMaxYPosition[0]) /
                          2,
                        tooltip: 'Center Point',
                        reverse: true,
                      },
                    ]}
                  />
                </Section>
                <Section>
                  <SectionCaption>Horizontal Position</SectionCaption>
                  <SliderSelect
                    title="Position"
                    Icon={
                      <div style={{ rotate: '90deg', paddingTop: '5px' }}>
                        <UpDownIcon />
                      </div>
                    }
                    action={handleSliderTextPosition}
                    defaultValue="50"
                    unit="%"
                    currValue={parseInt(
                      textPositionEffective.x || karaokeElement.x || '50',
                    )}
                    propertyName="x"
                    min={minMaxXPosition[0]}
                    max={minMaxXPosition[1]}
                    snapPoints={[
                      {
                        value: (minMaxXPosition[0] + minMaxXPosition[1]) / 2,
                        tooltip: 'Center Point',
                      },
                    ]}
                  />
                </Section>
              </SliderGroup>
              {sceneCount > 1 && (
                <SliderGroup>
                  <Section>
                    <Checkbox
                      label="Adjust by Scene"
                      color={
                        hasSceneOverride ? 'var(--primary)' : 'transparent'
                      }
                      borderColor={
                        hasSceneOverride
                          ? 'var(--primary)'
                          : 'var(--text-muted)'
                      }
                      labelColor="var(--text-secondary)"
                      checked={hasSceneOverride}
                      onClick={() => setSceneOverride(!hasSceneOverride)}
                    />
                  </Section>
                </SliderGroup>
              )}
              <SliderGroup>
                <Section>
                  <SectionCaption>Alignment</SectionCaption>
                  <SectionGroup style={{ gap: '8px' }}>
                    <AlignFormatting
                      alignType={effectiveAlignType}
                      toggleAlign={align => {
                        updateTextPosition(
                          'x_alignment',
                          AlignValues[align],
                          () => toggleAlign(align),
                        );
                      }}
                    />
                  </SectionGroup>
                </Section>
              </SliderGroup>
            </CollapsibleContent>
          )}
        </CollapsibleSection>

        {/* Advanced styling */}

        <CollapsibleSection>
          <CollapsibleHeader
            isOpen={openSections[COLLAPSIBLE_SECTIONS.advancedStyling]}
            onClick={() => toggleSection(COLLAPSIBLE_SECTIONS.advancedStyling)}
          >
            <CollapsibleTitle>Advanced Styling</CollapsibleTitle>
            <ChevronDownIcon
              width="12"
              height="12"
              strokeColor="currentColor"
            />
          </CollapsibleHeader>
          {openSections[COLLAPSIBLE_SECTIONS.advancedStyling] && (
            <CollapsibleContent>
              <SliderGroup style={{ justifyContent: 'flex-start' }}>
                <Section
                  style={{
                    flexDirection: 'row',
                    gap: '8px',
                    width: 'calc(50% - 6px)',
                  }}
                >
                  <Section>
                    <SectionCaption>Line crop</SectionCaption>
                    <AlignThresholdControl
                      backgroundAlignThreshold={
                        selectedBackgroundAlignThreshold
                      }
                      action={modifyPropertyWithHeadless}
                    />
                  </Section>
                  <Section>
                    <SectionCaption>Shadow</SectionCaption>
                    <KaraokeShadow
                      action={modifyPropertyWithHeadless}
                      value={selectedShadow || undefined}
                    />
                  </Section>
                </Section>
                {/* 84px, not 80: "Corner Radius" measures 82px at the caption's
                    --text-xs / medium, so an 80px cap wrapped it onto two lines
                    by two pixels. */}
                <Section style={{ maxWidth: '84px', placeSelf: 'flex-start' }}>
                  <SectionCaption>Corner Radius</SectionCaption>
                  <TextBackgroundRadius
                    action={modifyPropertyWithHeadless}
                    value={selectedBorderRadius}
                  />
                </Section>
              </SliderGroup>
              <SliderGroup>
                <Section>
                  <SectionCaption>Stroke Color</SectionCaption>
                  <Stroke>
                    <ColorDropdownSelect
                      action={(propertyName, value) => {
                        const propChange = {
                          [propertyName]: value,
                        };
                        if (
                          !value ||
                          value === 'transparent' ||
                          value === 'rgba(0, 0, 0, 0)'
                        ) {
                          propChange.stroke_color = 'transparent';
                          propChange.stroke_width = '0';
                          propChange.letter_spacing = '0%';
                        } else if (
                          !karaokeElement.stroke_color ||
                          karaokeElement.stroke_color === 'transparent' ||
                          karaokeElement.stroke_color === 'rgba(0, 0, 0, 0)'
                        ) {
                          propChange.stroke_width = '1 vmin';
                          propChange.letter_spacing = '50%';
                        }
                        modifyPropertyWithHeadless(propChange);
                      }}
                      propertyName="stroke_color"
                      defaultValue={strokeColor}
                    />
                  </Stroke>
                </Section>
                <Section>
                  <SectionCaption>Stroke Width</SectionCaption>
                  <StrokeSelect
                    modifyProperty={modifyProperty}
                    strokeWidth={strokeWidth}
                    hasStickyBottom={hasStickyBottom}
                  />
                </Section>
              </SliderGroup>
              <SliderGroup>
                <Section>
                  <SectionCaption>Animation Type</SectionCaption>
                  <ItemDropdownSelect
                    action={(_propertyName, value) =>
                      setAnimation('start', value || 'none', 1)
                    }
                    propertyName="animation_type"
                    defaultValue={animations?.enter || null}
                    values={[
                      { value: null, caption: 'None' },
                      ...Object.entries(TextAnimationTypes).map(
                        ([type, caption]) => ({
                          value: type,
                          caption,
                        }),
                      ),
                    ]}
                  />
                </Section>
              </SliderGroup>
            </CollapsibleContent>
          )}
        </CollapsibleSection>
      </Main>
    );
  }),
);

const Main = styled.div`
  display: flex;
  flex-direction: column;
`;
const ActionButton = styled.button<{ isSelected: boolean }>`
  outline: 0;
  border: 1px solid
    ${props => (props.isSelected ? 'var(--accent)' : 'var(--border)')};
  // width: 32px;
  width: 12%;
  aspect-ratio: 1/1;
  // height: 32px;
  background-color: ${props =>
    props.isSelected ? 'var(--accent)' : 'var(--background)'};
  border-radius: var(--radius);
  color: ${props => (props.isSelected ? 'var(--background)' : 'var(--border)')};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  /*
   * Hover follows the bordered-field-primitive standard
   * (docs/design-system.md) but only escalates when inactive — the
   * selected state already fills with accent, so escalating it to
   * accent again would be a no-op. Skip focus-visible accent border
   * here too: accent is reserved for the *selected* semantic state on
   * toggle buttons; using it for keyboard focus would confuse users
   * into thinking an unselected button is selected.
   */
  transition: border-color var(--transition-fast);
  &:hover:not(:disabled) {
    border-color: ${props =>
      props.isSelected ? 'var(--accent)' : 'var(--text-muted)'};
  }

  span.filler {
    font-weight: var(--font-weight-normal);
    font-size: 11px;
    line-height: 9.68px;
    color: ${props =>
      props.isSelected ? 'var(--background)' : 'var(--foreground)'};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 5px;
  // justify-content: space-between;
  align-items: center;
`;

export const SliderGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const SectionGroup = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (max-width: 1050px) {
    flex-direction: column;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
`;

const Highlight = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
`;

const Stroke = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollapsibleSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollapsibleHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  /* 22px was off the spacing scale and wider than the panel card's own 16px
     body padding, so these rows sat inset from everything around them. 16px
     vertical also made each collapsed row ~52px tall. */
  padding: var(--spacing-sm) var(--spacing-md);
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  /* One divider, not two. Every header drew both a top and a bottom border, so
     between two adjacent sections they stacked into a doubled 2px line — the
     heavy rules in the collapsed list. --border-subtle-lighter is also
     documented as "even lighter for tables", so the general --border-subtle is
     the right token here. */
  border-bottom: 1px solid var(--border-subtle);
  background: transparent;
  /* --text-muted measures 4.0:1 on this background, below AA for 12px text, and
     is why these read as faint. --text-secondary is 8.3:1 and matches the group
     labels elsewhere in the rail. */
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  line-height: 20px;
  cursor: pointer;
  transition: background var(--transition-fast);
  /* Row hover is --surface-elevated. This was using a *border* token as a
     background fill. */
  &:hover {
    background: var(--surface-elevated);
  }

  svg {
    margin-left: auto;
    transition: transform var(--transition-normal);
    transform: ${({ isOpen }) => (isOpen ? 'rotate(-180deg)' : 'rotate(0deg)')};
  }
`;

const CollapsibleTitle = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
`;

const CollapsibleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0px;
`;

export const SectionCaption = styled.span`
  font-family: var(--font-primary);
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;
